import React from 'react';
import { Link } from 'react-router-dom';
import { ParseReactElementsType } from '.';
import { NewTabLink } from '../../Shared/NewTabLink';
import { Nullable } from 'V2/Types';

/**
 * Loop through the array of content or children and parse
 *
 * @param elements Content from document to parse -- array of object: { tag, text, children, attributes, type }
 * @param options Extra options for parser -- defaults to { convertAnchorsToRouterLinks: true }
 * @returns Array of React Nodes
 */
export const parseReactElements: ParseReactElementsType = (
  elements,
  options = { convertAnchorsToRouterLinks: true }
) =>
  elements.map((element, idx) => {
    const { tag, text, children, attributes, type } = element;
    const childValue = !!children && parseReactElements(children, options);

    if (!!text) return text;

    const href = attributes?.href as Nullable<string>;
    const isValidHref = typeof href === 'string' && href.length > 0;
    const emailOrTel =
      tag === 'a' &&
      isValidHref &&
      (href.startsWith('mailto:') || href.startsWith('tel:'));
    const isAbsoluteUrl =
      tag === 'a' &&
      isValidHref &&
      (href.startsWith('http://') || href.startsWith('https://'));

    if (options.convertAnchorsToRouterLinks && tag === 'a' && !emailOrTel) {
      if (attributes && isValidHref) {
        const { href: hrefAttr, ...rest } = attributes;
        // hrefAttr is guaranteed to be string because isValidHref is true
        const hrefString = hrefAttr as string;

        // For absolute URLs, use a regular anchor tag instead of React Router Link
        if (isAbsoluteUrl) {
          return (
            <a href={hrefString} key={`${tag}-${idx}`} {...rest}>
              {childValue}
            </a>
          );
        }

        const urlQuery = new URLSearchParams(hrefString);
        const documentId = urlQuery.get('document');

        if (type === 'pdf') {
          const testId = childValue
            .toString()
            .toLowerCase()
            .replace(' ', '-')
            .concat('-link');
          // Route to new link in new tab
          return (
            <NewTabLink
              key={`${tag}-${idx}`}
              to={hrefString}
              id={testId}
              rest={rest}
              linkText={childValue.toString()}
            />
          );
        }

        return (
          <Link
            to={hrefString}
            key={`${tag}-${idx}`}
            data-testid={documentId}
            {...rest}
          >
            {childValue}
          </Link>
        );
      }
    }

    return (
      !!tag &&
      React.createElement(
        tag,
        { ...attributes, key: `${tag}-${idx}` },
        childValue
      )
    );
  });
